import { useMemo, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { api, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import {
  ShoppingCart,
  Loader2,
  Trash2,
  Calendar,
  Mail,
  Phone,
  Building2,
  Package2,
  MapPin,
  ClipboardList,
} from "lucide-react";
import { Textarea } from "@/components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";

const STATUS_OPTIONS = [
  { value: "new", label: "New" },
  { value: "confirmed", label: "Confirmed" },
  { value: "processing", label: "Processing" },
  { value: "ready_to_ship", label: "Ready to Ship" },
  { value: "shipped", label: "Shipped" },
  { value: "delivered", label: "Delivered" },
  { value: "cancelled", label: "Cancelled" },
];

const PAYMENT_OPTIONS = [
  { value: "pending", label: "Pending" },
  { value: "quoted", label: "Quoted" },
  { value: "paid", label: "Paid" },
  { value: "refunded", label: "Refunded" },
];

const STATUS_BADGE = {
  new: "bg-[var(--rsi-purple)] text-white",
  confirmed: "bg-sky-500 text-white",
  processing: "bg-amber-500 text-white",
  ready_to_ship: "bg-indigo-500 text-white",
  shipped: "bg-[var(--rsi-green)] text-white",
  delivered: "bg-emerald-600 text-white",
  cancelled: "bg-gray-400 text-white",
};

function formatDate(iso) {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleString();
  } catch {
    return iso;
  }
}

export default function AdminOrders() {
  const qc = useQueryClient();
  const [selected, setSelected] = useState(null);
  const [filter, setFilter] = useState("all");
  const [search, setSearch] = useState("");

  const { data: orders = [], isLoading } = useQuery({
    queryKey: ["admin-orders"],
    queryFn: async () => (await api.get("/admin/orders")).data,
  });

  const updateMut = useMutation({
    mutationFn: async ({ id, body }) => (await api.put(`/admin/orders/${id}`, body)).data,
    onSuccess: (updated) => {
      qc.invalidateQueries({ queryKey: ["admin-orders"] });
      qc.invalidateQueries({ queryKey: ["admin-dashboard"] });
      setSelected(updated);
      toast.success("Order updated");
    },
    onError: (error) => toast.error(formatApiError(error)),
  });

  const delMut = useMutation({
    mutationFn: async (id) => api.delete(`/admin/orders/${id}`),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["admin-orders"] });
      qc.invalidateQueries({ queryKey: ["admin-dashboard"] });
      setSelected(null);
      toast.success("Order deleted");
    },
    onError: (error) => toast.error(formatApiError(error)),
  });

  const filteredOrders = useMemo(() => {
    const normalizedSearch = search.trim().toLowerCase();
    return orders.filter((order) => {
      const matchesFilter = filter === "all" || order.status === filter;
      const haystack = [
        order.order_number,
        order.customer_name,
        order.company_name,
        order.email,
        order.phone,
        order.country,
        order.city,
        order.tracking_id,
      ]
        .filter(Boolean)
        .join(" ")
        .toLowerCase();
      return matchesFilter && (!normalizedSearch || haystack.includes(normalizedSearch));
    });
  }, [orders, filter, search]);

  const stats = useMemo(
    () => ({
      total: orders.length,
      newCount: orders.filter((order) => order.status === "new").length,
      active: orders.filter((order) => !["delivered", "cancelled"].includes(order.status)).length,
      shipped: orders.filter((order) => order.status === "shipped").length,
    }),
    [orders]
  );

  return (
    <div data-testid="admin-orders">
      <div className="flex flex-wrap items-end justify-between gap-4 mb-8">
        <div>
          <span className="badge-purple">
            <ShoppingCart className="h-3.5 w-3.5" /> App Commerce
          </span>
          <h1 className="font-display text-4xl font-black tracking-tighter mt-3 text-[var(--rsi-navy)]">
            Orders
          </h1>
          <p className="text-gray-600 text-sm mt-1">
            Orders placed from the customer app appear here for review, confirmation, and processing.
          </p>
        </div>
      </div>

      <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
        <div className="rsi-card p-5">
          <div className="text-xs uppercase tracking-wider text-gray-400">Total Orders</div>
          <div className="mt-2 font-display text-3xl font-black tracking-tight text-[var(--rsi-navy)]">{stats.total}</div>
        </div>
        <div className="rsi-card p-5">
          <div className="text-xs uppercase tracking-wider text-gray-400">New Orders</div>
          <div className="mt-2 font-display text-3xl font-black tracking-tight text-[var(--rsi-navy)]">{stats.newCount}</div>
        </div>
        <div className="rsi-card p-5">
          <div className="text-xs uppercase tracking-wider text-gray-400">Active Pipeline</div>
          <div className="mt-2 font-display text-3xl font-black tracking-tight text-[var(--rsi-navy)]">{stats.active}</div>
        </div>
        <div className="rsi-card p-5">
          <div className="text-xs uppercase tracking-wider text-gray-400">Shipped</div>
          <div className="mt-2 font-display text-3xl font-black tracking-tight text-[var(--rsi-navy)]">{stats.shipped}</div>
        </div>
      </div>

      <div className="flex flex-wrap gap-3 items-center justify-between mb-5">
        <div className="flex flex-wrap gap-2">
          {[
            { value: "all", label: "All" },
            ...STATUS_OPTIONS,
          ].map((option) => (
            <button
              key={option.value}
              type="button"
              onClick={() => setFilter(option.value)}
              className={`px-3 py-1.5 rounded-full text-xs font-semibold ${
                filter === option.value
                  ? "bg-[var(--rsi-purple)] text-white"
                  : "bg-white border border-gray-200 text-gray-700"
              }`}
            >
              {option.label}
            </button>
          ))}
        </div>

        <Input
          value={search}
          onChange={(event) => setSearch(event.target.value)}
          placeholder="Search order, customer, country, tracking..."
          className="w-full max-w-md"
        />
      </div>

      <div className="rsi-card overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-[var(--rsi-bg-subtle)] text-left">
              <tr>
                <th className="p-4 font-semibold text-[var(--rsi-navy)]">Order</th>
                <th className="p-4 font-semibold text-[var(--rsi-navy)]">Customer</th>
                <th className="p-4 font-semibold text-[var(--rsi-navy)]">Items</th>
                <th className="p-4 font-semibold text-[var(--rsi-navy)]">Status</th>
                <th className="p-4 font-semibold text-[var(--rsi-navy)]">Placed</th>
                <th className="p-4" />
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {isLoading ? (
                <tr>
                  <td colSpan={6} className="p-8 text-center">
                    <Loader2 className="h-5 w-5 animate-spin inline text-[var(--rsi-purple)]" />
                  </td>
                </tr>
              ) : filteredOrders.length === 0 ? (
                <tr>
                  <td colSpan={6} className="p-8 text-center text-gray-500">
                    No orders match the current filter.
                  </td>
                </tr>
              ) : (
                filteredOrders.map((order) => (
                  <tr
                    key={order.id}
                    className="hover:bg-gray-50 cursor-pointer"
                    onClick={() => setSelected(order)}
                    data-testid={`order-row-${order.id}`}
                  >
                    <td className="p-4">
                      <div className="font-mono-plex font-semibold text-[var(--rsi-navy)]">{order.order_number}</div>
                      <div className="text-xs text-gray-500 mt-1">{order.tracking_id || "Tracking pending"}</div>
                    </td>
                    <td className="p-4">
                      <div className="font-semibold text-[var(--rsi-navy)]">{order.customer_name}</div>
                      <div className="text-xs text-gray-500">{order.company_name || order.email}</div>
                    </td>
                    <td className="p-4 text-gray-600">
                      {order.total_line_items} lines · {order.total_units} units
                    </td>
                    <td className="p-4">
                      <span className={`text-[10px] font-semibold px-2 py-0.5 rounded-full uppercase tracking-wider ${STATUS_BADGE[order.status]}`}>
                        {order.status.replaceAll("_", " ")}
                      </span>
                    </td>
                    <td className="p-4 text-xs text-gray-500 font-mono-plex">{formatDate(order.created_at)}</td>
                    <td className="p-4 text-right">
                      <button
                        type="button"
                        onClick={(event) => {
                          event.stopPropagation();
                          if (window.confirm(`Delete order ${order.order_number}?`)) delMut.mutate(order.id);
                        }}
                        className="h-9 w-9 rounded-lg grid place-items-center hover:bg-red-50 text-gray-600 hover:text-red-600"
                      >
                        <Trash2 className="h-4 w-4" />
                      </button>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>

      <Dialog open={!!selected} onOpenChange={(open) => !open && setSelected(null)}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto bg-white">
          {selected ? (
            <>
              <DialogHeader>
                <DialogTitle className="font-display text-2xl font-extrabold tracking-tight text-[var(--rsi-navy)]">
                  Order {selected.order_number}
                </DialogTitle>
              </DialogHeader>

              <div className="mt-4 grid sm:grid-cols-2 gap-4">
                <div>
                  <div className="text-xs text-gray-400 uppercase tracking-wider">Customer</div>
                  <div className="flex items-center gap-2 mt-1 text-[var(--rsi-navy)] font-medium">
                    <Building2 className="h-4 w-4 text-gray-400" /> {selected.customer_name}
                  </div>
                  <div className="text-sm text-gray-500 mt-1">{selected.company_name || "No company name provided"}</div>
                </div>
                <div>
                  <div className="text-xs text-gray-400 uppercase tracking-wider">Contact</div>
                  <a href={`mailto:${selected.email}`} className="flex items-center gap-2 mt-1 text-[var(--rsi-purple)] font-medium">
                    <Mail className="h-4 w-4" /> {selected.email}
                  </a>
                  <div className="flex items-center gap-2 mt-2 text-[var(--rsi-navy)] font-medium">
                    <Phone className="h-4 w-4 text-gray-400" /> {selected.phone || "—"}
                  </div>
                </div>
                <div>
                  <div className="text-xs text-gray-400 uppercase tracking-wider">Location</div>
                  <div className="flex items-center gap-2 mt-1 text-[var(--rsi-navy)] font-medium">
                    <MapPin className="h-4 w-4 text-gray-400" /> {selected.city || "—"}, {selected.country || "—"}
                  </div>
                  <div className="text-sm text-gray-500 mt-1 whitespace-pre-line">{selected.address || "—"}</div>
                </div>
                <div>
                  <div className="text-xs text-gray-400 uppercase tracking-wider">Order Meta</div>
                  <div className="flex items-center gap-2 mt-1 text-[var(--rsi-navy)] font-medium">
                    <Calendar className="h-4 w-4 text-gray-400" /> {formatDate(selected.created_at)}
                  </div>
                  <div className="mt-2 text-sm text-gray-500">Preferred contact: {selected.preferred_contact}</div>
                  <div className="text-sm text-gray-500">Source: {selected.source}</div>
                </div>
              </div>

              <div className="mt-6">
                <div className="text-xs text-gray-400 uppercase tracking-wider mb-2">Ordered Items</div>
                <div className="space-y-3">
                  {selected.items.map((item, index) => (
                    <div key={`${item.product_id}-${index}`} className="rounded-2xl border border-gray-100 p-4 flex gap-4">
                      <img src={item.image_url} alt={item.product_name} className="h-16 w-16 rounded-2xl object-cover bg-gray-100" />
                      <div className="min-w-0 flex-1">
                        <div className="font-semibold text-[var(--rsi-navy)]">{item.product_name}</div>
                        <div className="text-xs text-gray-500 mt-1">{item.category_slug || "General"} · {item.price_label || "Price on request"}</div>
                        <div className="text-sm text-gray-600 mt-2">
                          Quantity: <span className="font-semibold text-[var(--rsi-navy)]">{item.quantity}</span>
                        </div>
                        {item.note ? <div className="text-sm text-gray-600 mt-1">Note: {item.note}</div> : null}
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              <div className="mt-6 grid sm:grid-cols-2 gap-4">
                <div>
                  <div className="text-xs text-gray-400 uppercase tracking-wider mb-1.5">Order Status</div>
                  <Select
                    value={selected.status}
                    onValueChange={(value) => updateMut.mutate({ id: selected.id, body: { status: value } })}
                  >
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {STATUS_OPTIONS.map((option) => (
                        <SelectItem key={option.value} value={option.value}>{option.label}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <div className="text-xs text-gray-400 uppercase tracking-wider mb-1.5">Payment Status</div>
                  <Select
                    value={selected.payment_status}
                    onValueChange={(value) => updateMut.mutate({ id: selected.id, body: { payment_status: value } })}
                  >
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {PAYMENT_OPTIONS.map((option) => (
                        <SelectItem key={option.value} value={option.value}>{option.label}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>

              <div className="mt-6">
                <div className="text-xs text-gray-400 uppercase tracking-wider mb-1.5">Tracking ID</div>
                <div className="flex gap-3">
                  <Input
                    value={selected.tracking_id || ""}
                    onChange={(event) => setSelected({ ...selected, tracking_id: event.target.value.toUpperCase() })}
                    placeholder="Assign tracking ID when shipment is created"
                  />
                  <button
                    type="button"
                    onClick={() => updateMut.mutate({ id: selected.id, body: { tracking_id: selected.tracking_id } })}
                    className="btn-primary"
                  >
                    Save
                  </button>
                </div>
              </div>

              <div className="mt-6">
                <div className="text-xs text-gray-400 uppercase tracking-wider mb-1.5">Customer Notes</div>
                <div className="rounded-2xl bg-[var(--rsi-bg-subtle)] p-4 text-sm text-gray-700 whitespace-pre-line">
                  {selected.notes || "No customer notes provided."}
                </div>
              </div>

              <div className="mt-6">
                <div className="text-xs text-gray-400 uppercase tracking-wider mb-1.5">Admin Notes</div>
                <Textarea
                  rows={4}
                  value={selected.admin_notes || ""}
                  onChange={(event) => setSelected({ ...selected, admin_notes: event.target.value })}
                  placeholder="Internal order handling notes..."
                />
                <button
                  type="button"
                  onClick={() => updateMut.mutate({ id: selected.id, body: { admin_notes: selected.admin_notes } })}
                  className="btn-primary mt-3"
                >
                  <ClipboardList className="h-4 w-4" /> Save notes
                </button>
              </div>
            </>
          ) : null}
        </DialogContent>
      </Dialog>
    </div>
  );
}
